import camelcase from 'camelcase';
import { createAction, createReducer } from 'deox';
import {
  defaultTo,
  find,
  has,
  lensProp,
  mergeDeepRight,
  mergeDeepWith,
  propEq,
  set,
} from 'ramda';
import {
  MountedState as CreditAppMountedState,
  selectCreditApplication,
} from 'state/creditApplication';
import {
  MountedState as InstantCreditMountedState,
  selectInstantCredit,
} from 'state/instantCredit';

export interface Disclosure {
  type: 'pre' | 'allInOne' | 'accountAssure' | `prequalification`;
  url: string;
}

export interface FieldCustomizationItem {
  optional: boolean;
  visible: boolean;
}

export interface LoyaltyNumberFieldCustomizationItem {
  optional: boolean;
}

export interface FieldCustomization {
  emailAddress: FieldCustomizationItem;
  annualIncome: FieldCustomizationItem;
  storeNumber: FieldCustomizationItem;
  intendedPurchaseAmount: FieldCustomizationItem;
  referralCode: FieldCustomizationItem;
  ssn: FieldCustomizationItem & { lastFourOnly: boolean };
  loyaltyNumber: LoyaltyNumberFieldCustomizationItem;
}

export interface CardChoice {
  imageUrl: string;
  name: string;
  code: number;
}

export interface ProductChoice {
  key: string;
  label: string;
  imageUrl: string;
  cardType: string;
  overrides: Partial<Tenant>;
}

export interface EnhancedPresentment {
  frameAncestors: string[];
}

export interface TextToRecover {
  type: 'accountNumber' | 'accountId';
}

export interface TSPDisplay {
  topExpireMessage: boolean;
  youWillReceiveMessage: boolean;
  creditLimit: boolean;
  nameOnCard: boolean;
  accountNumber: boolean;
  temporaryAccountNumber: boolean;
  temporaryShoppingPassLimit: boolean;
  temporaryExpirationDate: boolean;
  temporarySecurityCode: boolean;
  useWhenCheckoutMessage: boolean;
  transactionLimitMessage: boolean;
  barcode: boolean;
  wallet: boolean;
  aprMessage: boolean;
  discountOffer: boolean;
  registerAccountCenter: boolean;
  textToRecoverAsPrimaryButton: boolean;
}

export interface DSPOverride {
  tspDisplay?: TSPDisplay;
  tspIcon?: string;
  completionIcon?: string;
}

export interface OptInOut {
  type: 'optIn' | 'optOut';
  optOutCardType: string;
}

export interface Wallet {
  type: 'accountNumber' | 'callId';
}

export interface DspRetrieval {
  type: 'phoneLookupOnly' | 'phoneAndTokenLookup';
}

export interface Rewards {
  maxSelection: number | null; // 0 or greater, or null for "infinity"
  display: {
    barcode: boolean;
    certificateId: boolean;
  };
}

export interface BatchPrescreenOption {
  imageUrl: string;
}

export interface PrequalOption {
  imageUrl: string;
}
export interface EmployeeApplication {
  storeNumber: string;
  allowedIps: string[];
}

export interface LoyaltyNumberAgeCheck {
  minimumAge: number;
  underAgeLoyaltyNumber: string;
}

export interface TwoWayHybridApplyEcho {
  postUrl: string;
}

export interface PixelTracking {
  type: 'conversant';
}

export interface FeatureMap {
  tokenLookup: boolean | undefined;
  phoneLookup: boolean | undefined;
  enhancedCheckoutPhoneLookup: boolean | undefined;
  enhancedCheckoutTokenLookup: boolean | undefined;
  membershipValidation: boolean | undefined;
  inStore: boolean | undefined;
  dspPush: boolean | undefined;
  dspRetrieval: DspRetrieval | undefined;
  authBuyer: boolean | undefined;
  accountAssure: boolean | undefined;
  jointApplicant: boolean | undefined;
  prequalPhoneLookup: boolean | undefined;
  prequalTokenLookup: boolean | undefined;
  prequalOfferPush: boolean | undefined;
  batchPrescreen: boolean | undefined;
  prescreen: boolean | undefined;
  icDriverLicense: boolean | undefined;
  prequalDriverLicense: boolean | undefined;
  studentApplication: boolean | undefined;
  employeeApplication?: EmployeeApplication;
  storeAttribution: boolean;
  kioskMode: boolean;
  clerkMode: boolean;
  cardChoices: CardChoice[] | [];
  productChoices: ProductChoice[];
  batchPrescreenOption?: BatchPrescreenOption;
  prequalOption?: PrequalOption;
  textToRecover?: TextToRecover;
  enhancedPresentment?: EnhancedPresentment;
  optInOut?: OptInOut;
  wallet?: Wallet;
  rewards?: Rewards;
  enhancedCheckoutOption?: boolean | undefined;
  loyaltyNumberAgeCheck?: LoyaltyNumberAgeCheck | {};
  twoWayHybridApplyEcho?: TwoWayHybridApplyEcho;
  pixelTracking?: PixelTracking;
}

export const FeatureGroups = {
  'add-ons': ['authBuyer', 'accountAssure', 'jointApplicant'] as Array<
    keyof FeatureMap
  >,
  'consumer-search': ['tokenLookup', 'phoneLookup'] as Array<keyof FeatureMap>,
};

export interface Tenant {
  name: string;
  divisionNumber?: string;
  clientName: string;
  stringsFileName?: string;
  brandColor: string;
  logoUrl: string;
  barcodeFormat?: string;
  defaultStoreNumber: string;
  completionIcon?: string;
  tspIcon?: string;
  registrationUrl?: string;

  enabledFeatures: FeatureMap;

  productName?: {
    default: string;
    [key: string]: string | undefined;
  };

  tspDisplay: TSPDisplay;

  fieldCustomizations: FieldCustomization;

  disclosures: Disclosure[];

  trustedBrandPrefill: boolean;
  existingAccountBehavior: 'noDsp' | 'dsp' | 'validationRequired';

  session: {
    useInstantCreditTspTimeout: boolean;
    hideInstantCreditSessionWarning: boolean;
  };

  // TODO: deprecate
  geolocationAttribution?: {
    storeLocationsUrl: string;
    locationCheckPercentage: number;
  };

  analyticsReportSuiteId: string;

  overrideConfig: {
    production: boolean;
    stage: boolean;
    enhancedPresentment: boolean;
    dspPush: boolean;
    prescreen: boolean;
    batchPrescreen: boolean;
    prequalTokenLookup: boolean;
    prequalPhoneLookup: boolean;
    prequalOfferPush: boolean;
    enhancedCheckoutPhoneLookup: boolean;
    enhancedCheckoutTokenLookup: boolean;
    tokenLookup: boolean;
    phoneLookup: boolean;
    membershipValidation: boolean;
    inStore: boolean;
    enhancedCheckoutOption: boolean;
    kioskMode: boolean;
    clerkMode: boolean;
    employeeApplication: boolean;
    icDriverLicense: boolean;
    prequalDriverLicense: boolean;
  };

  dspOverrides?: {
    [key: string]: DSPOverride | undefined;
  };

  restrictedStates: string[];

  instantCreditClientToken: string;
}

const defaultValues = {
  fieldCustomizations: {},

  enabledFeatures: {
    tokenLookup: false,
    phoneLookup: false,
    dspPush: false,
    dspRetrieval: undefined,
    authBuyer: false,
    accountAssure: false,
    cardChoices: [],
    productChoices: [],
    storeAttribution: false,
    enhancedCheckoutOption: false,
    clerkMode: false,
  },

  session: {
    useInstantCreditTspTimeout: false,
    hideInstantCreditSessionWarning: false,
  },

  existingAccountBehavior: 'noDsp',

  restrictedStates: [],
};

/**
 * Tenant object with optional attributes that can be provided with
 * values from `defaultValues`
 */
export type PartialTenant = Omit<Tenant, keyof typeof defaultValues> &
  Partial<Pick<Tenant, keyof typeof defaultValues>>;

export interface TenantState {
  entity: Tenant | null;
}

export const setTenant = createAction(
  'SET_TENANT',
  resolve => (tenant: PartialTenant) => resolve({ tenant }),
);

const defaultState: TenantState = {
  entity: null,
};

const checkDisclosure = (tenant: Tenant): void => {
  const disclosures = tenant.disclosures;
  const pre = disclosures.find(({ type }) => type === 'pre');
  const allInOne = disclosures.find(({ type }) => type === 'allInOne');
  // We assume both of these are present, so let's throw an error early
  // if they are missing.
  if (!pre || !allInOne) {
    throw new Error('Tenant must have both pre and allInOne disclosures');
  }
};

export const tenantReducer = createReducer(defaultState, handleAction => [
  handleAction(setTenant, (state, { payload: { tenant } }) => {
    const fullTenant = mergeDeepWith(defaultTo, defaultValues, tenant);
    checkDisclosure(fullTenant);
    return set(lensProp('entity'), fullTenant, state);
  }),
]);

export interface MountedState {
  tenant: TenantState;
}

export const selectTenant = (
  state: MountedState & CreditAppMountedState & InstantCreditMountedState,
): Tenant => {
  let {
    tenant: { entity: tenant },
  } = state;
  const creditApplication = selectCreditApplication(state);
  const instantCredit = selectInstantCredit(state);

  if (!tenant) throw new Error('Tenant presence expected');

  if (creditApplication.productChoiceKey) {
    const choice = tenant.enabledFeatures.productChoices.find(
      ({ key }) => key === creditApplication.productChoiceKey,
    );
    tenant = choice ? { ...tenant, ...choice.overrides } : tenant;
  }

  if (instantCredit.cardType && tenant.dspOverrides) {
    // We deep camelize the tenant config when we receive it, so all object
    // keys, including nested ones, would have been processed with camelcase
    // first.
    const dspOverride = tenant.dspOverrides[camelcase(instantCredit.cardType)];
    if (dspOverride) {
      tenant = mergeDeepRight(tenant, dspOverride) as Tenant;
    }
  }
  return tenant;
};
export const isVisibleFn = (
  tenant: Tenant,
  name: keyof FieldCustomization,
): boolean => {
  const field = tenant.fieldCustomizations[name];
  return field && 'visible' in field ? field.visible : false;
};

export const isEnabledFn = (
  tenant: Tenant,
  feature: keyof FeatureMap,
): boolean => {
  if (!tenant.enabledFeatures) return false;
  if (!has(feature, tenant.enabledFeatures)) return false;
  const featureVal = tenant.enabledFeatures[feature];
  if (!featureVal) return false;
  if (Array.isArray(featureVal)) return Boolean(featureVal.length);
  if (typeof featureVal === 'object')
    return Boolean(Object.keys(featureVal).length);
  return true;
};

export const isGroupEnabledFn = (
  tenant: Tenant,
  group: Array<keyof FeatureMap>,
): boolean => {
  return group.some(feature => {
    return isEnabledFn(tenant, feature);
  });
};

export const getDisclosureUrl = (
  tenant: Tenant,
  type: Disclosure['type'],
): string => {
  const disclosure = find(propEq('type', type), tenant.disclosures);
  if (!disclosure) throw new Error('Disclosures presence expected');

  return disclosure.url;
};

export default tenantReducer;
